import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../contexts/ThemeProvider';
import { Mail, Lock, Eye, EyeOff, ChevronRight, Sun, Moon, X, ArrowLeft, CheckCircle2, AlertCircle, User } from 'lucide-react';
import { resolveApiUrl } from '../apiBase';
import LoginHeroPanel from './login/LoginHeroPanel';
import MfaChallengeCard from './login/MfaChallengeCard';
import VersionsVal, { FieldTipsRad } from './login/VersionsVal';
import { useVersionsVal } from '../utils/versionsVal';

export default function LoginPage() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Telefon: välj Field eller skrivbordsversionen före inloggningen (utils/versionsVal.js).
  const versionsVal = useVersionsVal(location);
  // Single source of truth for theme — shared with the rest of KanPlan via
  // ThemeProvider (localStorage 'kp-theme' + backend sync). Treat 'contrast'
  // as "dark visually" for the icon, but toggling on this page only flips
  // between 'dark' and 'light' since login lacks contrast styling.
  const { theme, setTheme } = useTheme();
  const darkMode = theme === 'dark' || theme === 'contrast';
  const setDarkMode = (next) => {
    const value = typeof next === 'function' ? next(darkMode) : next;
    setTheme(value ? 'dark' : 'light');
  };

  // UI state
  const [showPassword, setShowPassword] = useState(false);

  // Form state – pre-fill email from navigation state (e.g. after invite acceptance)
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Satt nar losenordet godkants men tvafaktor kravs. Ingen session finns an.
  const [mfaChallenge, setMfaChallenge] = useState('');

  // Microsoft-inloggning: visas bara nar organisationen har den pasla gen OCH
  // konfigurationen ar komplett. Backend svarar medvetet bara ja/nej.
  const [o365, setO365] = useState({ enabled: false, allow_password_login: true });

  useEffect(() => {
    let avbruten = false;
    (async () => {
      try {
        const slug = localStorage.getItem('kanplan-active-tenant-slug') || '';
        const bas = resolveApiUrl();
        const url = `${bas}/auth/o365/config${slug ? `?tenant_slug=${encodeURIComponent(slug)}` : ''}`;
        const r = await fetch(url, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (!avbruten) setO365(data);
      } catch {
        // Tyst: knappen uteblir, losenordsvagen finns kvar.
      }
    })();
    return () => { avbruten = true; };
  }, []);

  // Aterkomst fran Microsoft med fel — visa det dar anvandaren borjade.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const o365Fel = params.get('o365_fel');
      if (o365Fel) {
        setError(o365Fel);
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch { /* ignore */ }
  }, []);

  const startaMicrosoftInloggning = () => {
    try {
      const slug = localStorage.getItem('kanplan-active-tenant-slug') || '';
      const params = new URLSearchParams();
      if (slug) params.set('tenant_slug', slug);
      const postLogin = sessionStorage.getItem('postLoginRedirect') || '';
      if (postLogin && postLogin.startsWith('/') && !postLogin.startsWith('//')) {
        params.set('next', postLogin);
      }
      const fraga = params.toString();
      const bas = resolveApiUrl();
      window.location.href = `${bas}/auth/o365/start${fraga ? `?${fraga}` : ''}`;
    } catch {
      setError('Kunde inte starta Microsoft-inloggningen.');
    }
  };

  // Strip any stale `dark` class on <html> from earlier versions of this page
  // (ThemeProvider manages the canonical class on <body>).
  useEffect(() => {
    try { document.documentElement.classList.remove('dark'); } catch {}
  }, []);

  // Dit anvandaren ska efter lyckad inloggning. Delas av losenordsvagen och
  // tvafaktorsvagen sa att en omdirigering inte tappas bort i steg tva.
  const gaVidareEfterInloggning = () => {
    // If the user was redirected to /login due to a 401, send them back.
    // Otherwise, default to the Home Overview.
    let postLogin = '';
    try {
      postLogin = String(sessionStorage.getItem('postLoginRedirect') || '').trim();
      sessionStorage.removeItem('postLoginRedirect');
    } catch {}

    const safePostLogin = (
      postLogin &&
      postLogin.startsWith('/') &&
      !postLogin.startsWith('/login') &&
      !postLogin.startsWith('/skola')
    )
      ? postLogin
      : '';

    navigate(safePostLogin || '/home/overview', { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const svar = await login(email, password);

      // Tvafaktor kravs: stanna kvar och visa kodsteget. Ingen session finns an.
      if (svar && svar.mfa_required) {
        setMfaChallenge(svar.challenge || '');
        setPassword('');
        return;
      }

      gaVidareEfterInloggning();
    } catch (err) {
      setError(err?.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const onVerifieraKod = async (kod, reservkod) => {
    setError('');
    setBusy(true);
    try {
      await verifyMfa(mfaChallenge, kod, reservkod);
      gaVidareEfterInloggning();
    } catch (err) {
      setError(err?.message || 'Koden kunde inte verifieras.');
    } finally {
      setBusy(false);
    }
  };

  const avbrytTvafaktor = () => {
    setMfaChallenge('');
    setError('');
  };

  if (versionsVal.lage === 'field') return <Navigate to="/field" replace />;
  if (versionsVal.lage === 'val') return <VersionsVal onSkrivbord={versionsVal.visaInloggning} />;

  return (
    <div className="h-screen w-full flex flex-col font-inter bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900 dark:from-gray-900 dark:via-gray-950 dark:to-black dark:text-gray-100 transition-colors duration-500">
      <header className="flex-shrink-0 flex items-center justify-between px-6 md:px-10 h-14 border-b border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-gray-900/70 backdrop-blur-md z-10">
        <div className="flex items-center gap-3">
          <span
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 28, height: 28, borderRadius: 7,
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            }}
          >
            <svg viewBox="0 0 12 12" fill="none" width={16} height={16}>
              <path d="M2 5h3v5H2V5zm5-3h3v8H7V2z" fill="#fff" />
            </svg>
          </span>
          <span
            className="text-gray-900 dark:text-gray-100"
            style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}
          >
            KanPlan
          </span>
        </div>

        <button
          onClick={() => setDarkMode(!darkMode)}
          className="p-2 rounded-[6px] border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
          aria-label="Toggle dark mode"
        >
          {darkMode ? <Sun className="h-4 w-4 text-yellow-400" /> : <Moon className="h-4 w-4 text-gray-600" />}
        </button>
      </header>

      {/* Split on md+, centered on mobile */}
      <div className="hidden md:flex flex-1 min-h-0">
        <SplitLayout>
          {mfaChallenge ? (
            <MfaChallengeCard
              email={email}
              onVerify={onVerifieraKod}
              onCancel={avbrytTvafaktor}
              error={error}
              busy={busy}
            />
          ) : (
            <LoginCard
              o365={o365}
              onMicrosoftLogin={startaMicrosoftInloggning}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              onSubmit={onSubmit}
              busy={busy}
              error={error}
              clearError={() => setError('')}
              darkMode={darkMode}
              setDarkMode={setDarkMode}
            />
          )}
        </SplitLayout>
      </div>
      <div className="flex md:hidden flex-1 min-h-0">
        <CenteredLayout>
          {mfaChallenge ? (
            <MfaChallengeCard
              email={email}
              onVerify={onVerifieraKod}
              onCancel={avbrytTvafaktor}
              error={error}
              busy={busy}
            />
          ) : (
            <LoginCard
              o365={o365}
              onMicrosoftLogin={startaMicrosoftInloggning}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              onSubmit={onSubmit}
              busy={busy}
              error={error}
              clearError={() => setError('')}
              darkMode={darkMode}
              setDarkMode={setDarkMode}
            />
          )}
        </CenteredLayout>
      </div>

      <footer
        className="relative z-20 flex-shrink-0 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 flex items-center justify-between flex-wrap"
        style={{ minHeight: 48, padding: '8px 24px', fontFamily: "'Inter', sans-serif", gap: 8 }}
      >
        <div className="flex items-center" style={{ gap: 12 }}>
          <a href="/" className="flex items-center no-underline" style={{ gap: 7 }}>
            <span
              className="flex items-center justify-center"
              style={{
                width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              }}
            >
              <svg viewBox="0 0 12 12" fill="none" width={12} height={12}>
                <path d="M2 5h3v5H2V5zm5-3h3v8H7V2z" fill="#fff" />
              </svg>
            </span>
            <span
              className="text-slate-900 dark:text-slate-200"
              style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.02em' }}
            >
              KanPlan
            </span>
          </a>
          <span className="bg-slate-200 dark:bg-slate-700" style={{ width: 1, height: 16, flexShrink: 0 }} />
          <span className="text-slate-400 dark:text-slate-500" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
            {`© ${new Date().getFullYear()} KanPlan`}
          </span>
        </div>
        <FieldTipsRad />
      </footer>
    </div>
  );
}

function SplitLayout({ children }) {
  return (
    <div className="flex-1 min-h-0 grid grid-cols-2">
      <LoginHeroPanel />
      <div className="flex items-center justify-center px-6 py-8 overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

function CenteredLayout({ children }) {
  return (
    <div className="relative flex-1 min-h-0 flex items-center justify-center overflow-y-auto">
      <div className="pointer-events-none absolute -top-24 -left-24 h-80 w-80 rounded-full bg-[#1E3A8A]/20 blur-[80px] opacity-70 dark:bg-[#1E3A8A]/40" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-[#F97316]/20 blur-[80px] opacity-60 dark:bg-[#F97316]/40" />
      <svg className="absolute inset-0 opacity-[0.08] text-slate-400 dark:text-gray-500" viewBox="0 0 400 400" preserveAspectRatio="none">
        <defs>
          <pattern id="grid2" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 24" fill="none" stroke="currentColor" strokeWidth="0.8" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid2)" />
      </svg>
      <div className="relative p-6 sm:p-8">
        {children}
      </div>
    </div>
  );
}

function LoginCard({ o365 = {}, onMicrosoftLogin, showPassword, setShowPassword, email, setEmail, password, setPassword, onSubmit, busy, error, clearError, darkMode, setDarkMode }) {
  const [showStatus, setShowStatus] = useState(false);
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'forgot-sent' | 'request-account' | 'request-account-sent'
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [requestName, setRequestName] = useState('');
  const [requestEmail, setRequestEmail] = useState('');
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestError, setRequestError] = useState('');

  const supportMailto = buildSupportMailto({
    email,
    tenantSlug: getActiveTenantSlug(),
    lastError: error,
  });

  const onForgotSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setForgotError('');
    setForgotBusy(true);
    try {
      const apiUrl = resolveApiUrl();
      const res = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Något gick fel');
      }
      setView('forgot-sent');
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotBusy(false);
    }
  };

  const onRequestAccountSubmit = async (e) => {
    e.preventDefault();
    if (!requestName.trim() || !requestEmail.trim()) return;
    setRequestError('');
    setRequestBusy(true);
    try {
      const apiUrl = resolveApiUrl();
      const res = await fetch(`${apiUrl}/auth/request-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: requestName.trim(), email: requestEmail.trim() }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Något gick fel');
      }
      setView('request-account-sent');
    } catch (err) {
      setRequestError(err.message);
    } finally {
      setRequestBusy(false);
    }
  };

  /* ── Request account: sent confirmation ── */
  if (view === 'request-account-sent') {
    return (
      <div className="w-[min(440px,90vw)] rounded-[12px] border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-gray-900/95 shadow-[0_4px_20px_rgba(30,41,59,0.10)] flex flex-col" style={{ minHeight: 420 }}>
        <div className="p-6 sm:p-8 text-center flex-1 flex flex-col items-center justify-center">
          <CheckCircle2 className="mx-auto h-11 w-11 text-[#059669] dark:text-emerald-400 mb-4" />
          <div className="text-[17px] font-bold tracking-[-0.01em] text-[#1E293B] dark:text-gray-100 mb-2">Begäran mottagen!</div>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 leading-relaxed mb-6">
            Vi har tagit emot din begäran och skickat en bekräftelse till <strong className="text-[#1E293B] dark:text-gray-200">{requestEmail}</strong>. Vi återkommer inom kort.
          </p>
          <button
            type="button"
            onClick={() => { setView('login'); setRequestError(''); setRequestName(''); setRequestEmail(''); clearError && clearError(); }}
            className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#3B82F6] dark:text-[#F97316] hover:text-[#2563EB] hover:underline transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka till inloggning
          </button>
        </div>
      </div>
    );
  }

  /* ── Request account: form ── */
  if (view === 'request-account') {
    return (
      <div className="w-[min(440px,90vw)] rounded-[12px] border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-gray-900/95 shadow-[0_4px_20px_rgba(30,41,59,0.10)] flex flex-col" style={{ minHeight: 420 }}>
        <div className="p-6 sm:p-8 flex-1 flex flex-col justify-center">
          <div className="mb-6">
            <div className="text-[17px] font-bold tracking-[-0.01em] text-[#1E293B] dark:text-gray-100">Begär konto</div>
            <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1.5">Fyll i ditt namn och e-postadress så kontaktar vi dig inom kort.</p>
          </div>

          {requestError && (
            <div className="mb-3 flex items-center gap-2 text-[13px] text-[#DC2626] dark:text-[#F87171]">
              <AlertCircle className="h-4 w-4 flex-shrink-0" /> {requestError}
            </div>
          )}

          <form className="grid gap-4" onSubmit={onRequestAccountSubmit}>
            <label className="grid gap-1.5">
              <span className="text-[12px] font-semibold text-[#1E293B] dark:text-gray-300">Namn</span>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8D9DB0] dark:text-gray-500" />
                <input
                  type="text"
                  autoComplete="name"
                  className="w-full pl-10 pr-3 h-[42px] rounded-[10px] border-[1.5px] border-transparent bg-[#F0F2F5] dark:bg-gray-800 dark:border-gray-700 text-[13.5px] font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-[#8D9DB0] placeholder:font-normal focus:outline-none focus:border-[#3B82F6] focus:bg-white focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] dark:focus:border-[#F97316] dark:focus:shadow-[0_0_0_3px_rgba(249,115,22,0.2)] transition-all"
                  placeholder="Förnamn Efternamn"
                  value={requestName}
                  onChange={(e) => setRequestName(e.target.value)}
                  autoFocus
                />
              </div>
            </label>

            <label className="grid gap-1.5">
              <span className="text-[12px] font-semibold text-[#1E293B] dark:text-gray-300">E-post</span>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8D9DB0] dark:text-gray-500" />
                <input
                  type="email"
                  autoComplete="email"
                  className="w-full pl-10 pr-3 h-[42px] rounded-[10px] border-[1.5px] border-transparent bg-[#F0F2F5] dark:bg-gray-800 dark:border-gray-700 text-[13.5px] font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-[#8D9DB0] placeholder:font-normal focus:outline-none focus:border-[#3B82F6] focus:bg-white focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] dark:focus:border-[#F97316] dark:focus:shadow-[0_0_0_3px_rgba(249,115,22,0.2)] transition-all"
                  placeholder="namn@företag.se"
                  value={requestEmail}
                  onChange={(e) => setRequestEmail(e.target.value)}
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={requestBusy || !requestName.trim() || !requestEmail.trim()}
              className="group inline-flex items-center justify-center gap-2 w-full h-[44px] rounded-[6px] bg-[#3B82F6] dark:bg-[#F97316] px-5 text-[13.5px] text-white font-semibold shadow-[0_1px_3px_rgba(59,130,246,0.25)] hover:bg-[#2563EB] hover:shadow-[0_2px_8px_rgba(59,130,246,0.3)] dark:hover:bg-[#ea580c] focus:outline-none focus:ring-4 focus:ring-[#3B82F6]/25 dark:focus:ring-[#F97316]/30 disabled:opacity-60 transition-all"
            >
              {requestBusy ? 'Skickar…' : 'Skicka begäran'}
            </button>

            <button
              type="button"
              onClick={() => { setView('login'); setRequestError(''); clearError && clearError(); }}
              className="inline-flex items-center justify-center gap-2 text-[13px] font-semibold text-[#3B82F6] dark:text-[#F97316] hover:text-[#2563EB] hover:underline transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Tillbaka till inloggning
            </button>
          </form>
        </div>
      </div>
    );
  }

  /* ── Forgot password: sent confirmation ── */
  if (view === 'forgot-sent') {
    return (
      <div className="w-[min(440px,90vw)] rounded-[12px] border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-gray-900/95 shadow-[0_4px_20px_rgba(30,41,59,0.10)] flex flex-col" style={{ minHeight: 420 }}>
        <div className="p-6 sm:p-8 text-center flex-1 flex flex-col items-center justify-center">
          <CheckCircle2 className="mx-auto h-11 w-11 text-[#059669] dark:text-emerald-400 mb-4" />
          <div className="text-[17px] font-bold tracking-[-0.01em] text-[#1E293B] dark:text-gray-100 mb-2">Mejl skickat!</div>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 leading-relaxed mb-6">
            Om e-postadressen <strong className="text-[#1E293B] dark:text-gray-200">{email}</strong> finns i systemet har vi skickat en länk för att återställa lösenordet. Kolla din inkorg (och skräppost).
          </p>
          <button
            type="button"
            onClick={() => { setView('login'); setForgotError(''); clearError && clearError(); }}
            className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#3B82F6] dark:text-[#F97316] hover:text-[#2563EB] hover:underline transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka till inloggning
          </button>
        </div>
      </div>
    );
  }

  /* ── Forgot password: email form ── */
  if (view === 'forgot') {
    return (
      <div className="w-[min(440px,90vw)] rounded-[12px] border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-gray-900/95 shadow-[0_4px_20px_rgba(30,41,59,0.10)] flex flex-col" style={{ minHeight: 420 }}>
        <div className="p-6 sm:p-8 flex-1 flex flex-col justify-center">
          <div className="mb-6">
            <div className="text-[17px] font-bold tracking-[-0.01em] text-[#1E293B] dark:text-gray-100">Glömt lösenord?</div>
            <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1.5">Ange din e-postadress så skickar vi en länk för att välja nytt lösenord.</p>
          </div>

          {forgotError && (
            <div className="mb-3 flex items-center gap-2 text-[13px] text-[#DC2626] dark:text-[#F87171]">
              <AlertCircle className="h-4 w-4 flex-shrink-0" /> {forgotError}
            </div>
          )}

          <form className="grid gap-4" onSubmit={onForgotSubmit}>
            <label className="grid gap-1.5">
              <span className="text-[12px] font-semibold text-[#1E293B] dark:text-gray-300">E-post</span>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8D9DB0] dark:text-gray-500" />
                <input
                  type="email"
                  autoComplete="email"
                  className="w-full pl-10 pr-3 h-[42px] rounded-[10px] border-[1.5px] border-transparent bg-[#F0F2F5] dark:bg-gray-800 dark:border-gray-700 text-[13.5px] font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-[#8D9DB0] placeholder:font-normal focus:outline-none focus:border-[#3B82F6] focus:bg-white focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] dark:focus:border-[#F97316] dark:focus:shadow-[0_0_0_3px_rgba(249,115,22,0.2)] transition-all"
                  placeholder="namn@företag.se"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={forgotBusy || !email.trim()}
              className="group inline-flex items-center justify-center gap-2 w-full h-[44px] rounded-[6px] bg-[#3B82F6] dark:bg-[#F97316] px-5 text-[13.5px] text-white font-semibold shadow-[0_1px_3px_rgba(59,130,246,0.25)] hover:bg-[#2563EB] hover:shadow-[0_2px_8px_rgba(59,130,246,0.3)] dark:hover:bg-[#ea580c] focus:outline-none focus:ring-4 focus:ring-[#3B82F6]/25 dark:focus:ring-[#F97316]/30 disabled:opacity-60 transition-all"
            >
              {forgotBusy ? 'Skickar…' : 'Skicka återställningslänk'}
            </button>

            <button
              type="button"
              onClick={() => { setView('login'); setForgotError(''); clearError && clearError(); }}
              className="inline-flex items-center justify-center gap-2 text-[13px] font-semibold text-[#3B82F6] dark:text-[#F97316] hover:text-[#2563EB] hover:underline transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Tillbaka till inloggning
            </button>
          </form>
        </div>
      </div>
    );
  }

  /* ── Default: Login form ── */
  return (
    <div className="w-[min(440px,90vw)] rounded-[12px] border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-gray-900/95 shadow-[0_4px_20px_rgba(30,41,59,0.10)] flex flex-col" style={{ minHeight: 420 }}>
      <div className="p-6 sm:p-8 flex-1 flex flex-col justify-center">
        <div className="mb-6">
          <div className="text-[22px] font-bold tracking-[-0.01em] text-[#1E293B] dark:text-gray-100">Välkommen</div>
          <p className="text-[13.5px] text-[#64748B] dark:text-gray-400 mt-1.5">Logga in för att fortsätta till KanPlan.</p>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2.5 rounded-[10px] border border-[#FECACA] dark:border-red-800/60 bg-[#FEF2F2] dark:bg-red-950/40 px-4 py-3">
            <AlertCircle className="h-4 w-4 text-[#DC2626] dark:text-[#F87171] flex-shrink-0 mt-0.5" />
            <span className="text-[13px] leading-relaxed text-[#991B1B] dark:text-[#FCA5A5]">{error}</span>
          </div>
        )}

        {o365.enabled && (
          <div className="mb-5">
            <button
              type="button"
              onClick={onMicrosoftLogin}
              className="inline-flex items-center justify-center gap-2.5 w-full h-[44px] rounded-[6px] border border-[#E2E8F0] dark:border-gray-600 bg-white dark:bg-gray-800 px-5 text-[13.5px] font-semibold text-[#1E293B] dark:text-gray-100 hover:bg-[#F8FAFC] dark:hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-[#3B82F6]/20 transition-all"
            >
              <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
              Logga in med Microsoft
            </button>

            {o365.allow_password_login !== false && (
              <div className="flex items-center gap-3 mt-5">
                <div className="flex-1 h-px bg-[#E2E8F0] dark:bg-gray-700" />
                <span className="text-[11.5px] font-semibold uppercase tracking-[0.05em] text-[#8D9DB0] dark:text-gray-500">
                  eller
                </span>
                <div className="flex-1 h-px bg-[#E2E8F0] dark:bg-gray-700" />
              </div>
            )}
          </div>
        )}

        {o365.allow_password_login === false ? (
          <p className="text-[13px] leading-relaxed text-[#64748B] dark:text-gray-400">
            Din organisation använder Microsoft-inloggning. Kontakta din
            administratör om du inte kommer in.
          </p>
        ) : (
        <form className="grid gap-4" onSubmit={onSubmit}>
          <label className="grid gap-1.5">
            <span className="text-[12px] font-semibold text-[#1E293B] dark:text-gray-300">E-post</span>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8D9DB0] dark:text-gray-500" />
              <input
                type="email"
                autoComplete="username email"
                className="w-full pl-10 pr-3 h-[42px] rounded-[10px] border-[1.5px] border-transparent bg-[#F0F2F5] dark:bg-gray-800 dark:border-gray-700 text-[13.5px] font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-[#8D9DB0] placeholder:font-normal focus:outline-none focus:border-[#3B82F6] focus:bg-white focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] dark:focus:border-[#F97316] dark:focus:shadow-[0_0_0_3px_rgba(249,115,22,0.2)] transition-all"
                placeholder="namn@företag.se"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </label>

          <label className="grid gap-1.5">
            <span className="text-[12px] font-semibold text-[#1E293B] dark:text-gray-300">Lösenord</span>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8D9DB0] dark:text-gray-500" />
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="w-full pl-10 pr-10 h-[42px] rounded-[10px] border-[1.5px] border-transparent bg-[#F0F2F5] dark:bg-gray-800 dark:border-gray-700 text-[13.5px] font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-[#8D9DB0] placeholder:font-normal focus:outline-none focus:border-[#3B82F6] focus:bg-white focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] dark:focus:border-[#F97316] dark:focus:shadow-[0_0_0_3px_rgba(249,115,22,0.2)] transition-all"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-[4px] hover:bg-gray-100 dark:hover:bg-gray-700 text-[#8D9DB0] dark:text-gray-400 transition-colors"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </label>

          <div className="flex items-center justify-between text-[13px] pt-1">
            <label className="inline-flex items-center gap-2 select-none">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-[#3B82F6] dark:text-[#F97316] focus:ring-[#3B82F6] dark:focus:ring-[#F97316]" />
              <span className="text-[#475569] dark:text-gray-400">Kom ihåg mig</span>
            </label>
            <button type="button" onClick={() => { setView('forgot'); clearError && clearError(); }} className="text-[#3B82F6] dark:text-[#F97316] hover:text-[#2563EB] dark:hover:text-[#fb923c] font-semibold hover:underline transition-colors">Glömt lösenord?</button>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="group inline-flex items-center justify-center gap-2 w-full h-[44px] rounded-[6px] bg-[#3B82F6] dark:bg-[#F97316] px-5 text-[13.5px] text-white font-semibold shadow-[0_1px_3px_rgba(59,130,246,0.25)] hover:bg-[#2563EB] hover:shadow-[0_2px_8px_rgba(59,130,246,0.3)] dark:hover:bg-[#ea580c] focus:outline-none focus:ring-4 focus:ring-[#3B82F6]/25 dark:focus:ring-[#F97316]/30 disabled:opacity-60 transition-all"
          >
            {busy ? 'Loggar in…' : 'Logga in'}
            <ChevronRight className="h-4 w-4 opacity-90 group-hover:translate-x-0.5 transition-transform" />
          </button>

          <div className="mt-5 pt-4 border-t border-[#E2E8F0] dark:border-gray-700">
            <div className="text-[13px] text-[#1E293B] dark:text-gray-300 leading-relaxed">
              <div className="font-semibold">Inget konto eller problem att logga in?</div>
              <div className="text-[#475569] dark:text-gray-400 mt-0.5">Begär ett konto eller kontakta support.</div>
            </div>
            <div className="mt-3 flex items-center gap-4 text-[13px]">
              <button
                type="button"
                onClick={() => { setView('request-account'); clearError && clearError(); }}
                className="text-[#3B82F6] dark:text-[#F97316] hover:text-[#2563EB] hover:underline font-semibold transition-colors"
              >
                Begär konto
              </button>
              <a
                href={supportMailto}
                className="text-[#3B82F6] dark:text-[#F97316] hover:text-[#2563EB] hover:underline font-semibold transition-colors"
              >
                Kontakta support
              </a>
              <button
                type="button"
                onClick={() => setShowStatus(true)}
                className="text-[#3B82F6] dark:text-[#F97316] hover:text-[#2563EB] hover:underline font-semibold transition-colors"
              >
                Driftstatus
              </button>
            </div>
          </div>
        </form>
        )}
      </div>

      <StatusModal open={showStatus} onClose={() => setShowStatus(false)} supportMailto={supportMailto} darkMode={darkMode} onToggleDark={() => setDarkMode(d => !d)} />
    </div>
  );
}

function getActiveTenantSlug() {
  try {
    return String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim();
  } catch {
    return '';
  }
}

function buildSupportMailto({ email, tenantSlug, lastError } = {}) {
  try {
    const to = 'hej@kanplan.se';
    const subject = 'KanPlan support';
    const safeEmail = String(email || '').trim();
    const safeTenant = String(tenantSlug || '').trim();
    const safeErr = String(lastError || '').trim();
    const lines = [
      'Hej!',
      '',
      'Jag behöver hjälp med inloggning / åtkomst.',
      '',
      safeEmail ? `E-post/username: ${safeEmail}` : null,
      safeTenant ? `Tenant: ${safeTenant}` : null,
      safeErr ? `Felmeddelande: ${safeErr}` : null,
      `Tid: ${new Date().toISOString()}`,
      `Sida: ${typeof window !== 'undefined' ? window.location.href : ''}`,
      `Webbläsare: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`,
    ].filter(Boolean);
    const body = lines.join('\n');
    return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  } catch {
    return 'mailto:hej@kanplan.se';
  }
}

function StatusModal({ open, onClose, supportMailto, darkMode, onToggleDark }) {
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [portalRoot, setPortalRoot] = useState(null);
  const mapCanvasRef = useRef(null);
  const [bootDone, setBootDone] = useState(false);
  const [visibleLines, setVisibleLines] = useState(0);
  const [bootProgress, setBootProgress] = useState(0);

  // Portal setup
  useEffect(() => {
    if (!open) { setPortalRoot(null); setBootDone(false); setVisibleLines(0); setBootProgress(0); return; }
    if (typeof document === 'undefined') return;
    let el = document.getElementById('kanplan-modal-root');
    let created = false;
    if (!el) {
      el = document.createElement('div');
      el.id = 'kanplan-modal-root';
      document.body?.appendChild?.(el);
      created = true;
    }
    setPortalRoot(el instanceof HTMLElement ? el : null);
    return () => {
      if (created) { try { el?.parentNode?.removeChild?.(el); } catch {} }
    };
  }, [open]);

  // Boot animation sequence
  useEffect(() => {
    if (!open) return;
    setBootDone(false); setVisibleLines(0); setBootProgress(0);
    const delays = [100, 350, 650, 900, 1150, 1400, 1650, 1850, 2100];
    const bootEnd = 2600;
    const timers = [];
    delays.forEach((delay, i) => {
      timers.push(setTimeout(() => {
        setVisibleLines(i + 1);
        setBootProgress(Math.min(100, Math.round((delay / bootEnd) * 110)));
      }, delay));
    });
    timers.push(setTimeout(() => setBootProgress(100), bootEnd));
    timers.push(setTimeout(() => setBootDone(true), bootEnd + 400));
    return () => timers.forEach(clearTimeout);
  }, [open]);

  // Data fetching
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const apiUrl = resolveApiUrl();
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    const setStep = (key, patch) => {
      setSteps((prev) => {
        const idx = prev.findIndex((s) => s.key === key);
        if (idx === -1) return [...prev, { key, ...patch }];
        const next = prev.slice();
        next[idx] = { ...next[idx], ...patch };
        return next;
      });
    };

    const run = async () => {
      setRunning(true);
      setSteps([
        { key: 'backend', label: 'Backend Health', meta: 'GET /health', status: 'pending' },
        { key: 'network', label: 'Nätverk & klient', meta: '', status: 'pending' },
        { key: 'timing', label: 'Prestanda (lokalt)', meta: '', status: 'pending' },
      ]);
      setSummary(null);
      const startedAt = Date.now();
      const result = { apiUrl, backend: null, client: null, perf: null, security: null, infra: null, build: getFrontendBuildInfo() };

      try {
        // Backend health
        setStep('backend', { status: 'running' });
        await wait(250);
        const ctrl = new AbortController();
        const timeout = setTimeout(() => ctrl.abort(), 5000);
        const t0 = performance.now();
        let backendOk = false;
        let backendJson = null;
        let requestId = '';
        try {
          const res = await fetch(`${apiUrl}/health`, { signal: ctrl.signal, credentials: 'omit' });
          requestId = res.headers.get('x-request-id') || '';
          backendJson = await res.json().catch(() => null);
          backendOk = res.ok && backendJson && backendJson.status === 'ok';
          result.backend = { ok: backendOk, httpStatus: res.status, latencyMs: Math.round(performance.now() - t0), requestId, payload: backendJson };
        } catch (e) {
          result.backend = { ok: false, httpStatus: null, latencyMs: Math.round(performance.now() - t0), requestId, error: String(e?.name || e?.message || e) };
        } finally { clearTimeout(timeout); }
        const reqIdShort = result.backend.requestId ? result.backend.requestId.slice(0, 8) : '';
        setStep('backend', {
          status: result.backend.ok ? 'ok' : 'error',
          meta: 'GET /health' + (reqIdShort ? ` · ${reqIdShort}` : ''),
          detail: result.backend.ok ? `${result.backend.latencyMs} ms` : `Fel · ${result.backend.httpStatus ?? 'n/a'}`,
        });

        // Network & client
        setStep('network', { status: 'running' });
        await wait(450);
        const nav = typeof navigator !== 'undefined' ? navigator : null;
        const conn = nav && nav.connection ? nav.connection : null;
        const pageProtocol = typeof window !== 'undefined' ? window.location?.protocol : '';
        const apiProtocol = (() => { try { return new URL(apiUrl).protocol; } catch { return ''; } })();
        result.client = {
          online: typeof nav?.onLine === 'boolean' ? nav.onLine : null,
          connection: conn ? { effectiveType: conn.effectiveType } : null,
        };
        result.infra = { region: 'AWS eu-north-1 (Stockholm)' };
        result.security = {
          pageTransport: pageProtocol === 'https:' ? 'HTTPS' : pageProtocol || 'n/a',
          apiTransport: apiProtocol === 'https:' ? 'HTTPS' : apiProtocol || 'n/a',
          secureContext: typeof window !== 'undefined' ? Boolean(window.isSecureContext) : null,
          cookiesEnabled: typeof nav?.cookieEnabled === 'boolean' ? nav.cookieEnabled : null,
        };
        setStep('network', {
          status: 'ok',
          meta: result.client.online === false ? 'Offline' : 'Online',
        });

        // Timing / Perf
        setStep('timing', { status: 'running' });
        await wait(600);
        let perf = null;
        try {
          const navEntry = performance.getEntriesByType ? performance.getEntriesByType('navigation')?.[0] : null;
          perf = navEntry ? { domContentLoadedMs: Math.round(navEntry.domContentLoadedEventEnd), loadMs: Math.round(navEntry.loadEventEnd) } : null;
        } catch { perf = null; }
        result.perf = perf;
        setStep('timing', { status: 'ok', meta: perf ? `Load ~${perf.loadMs} ms` : 'Klar' });

        const totalMs = Date.now() - startedAt;
        setSummary({ ok: Boolean(result.backend?.ok), totalMs, result });
      } finally {
        if (!cancelled) setRunning(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open]);

  // Map rendering — re-trigger when bootDone so canvas is visible/sized
  useEffect(() => {
    const canvas = mapCanvasRef.current;
    if (!canvas || !open || !bootDone) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const isDk = darkMode;
    const tileBase = isDk ? 'https://basemaps.cartocdn.com/dark_all' : 'https://basemaps.cartocdn.com/light_all';
    const lat = 59.3327, lon = 18.0656, zoom = 4;
    const W = canvas.width, H = canvas.height, ts = 256;
    const lon2tile = (l, z) => ((l + 180) / 360) * Math.pow(2, z);
    const lat2tile = (l, z) => (1 - Math.log(Math.tan(l * Math.PI / 180) + 1 / Math.cos(l * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, z);
    const cx = lon2tile(lon, zoom), cy = lat2tile(lat, zoom);
    const nx = Math.ceil(W / ts) + 1, ny = Math.ceil(H / ts) + 1;
    const sx = Math.floor(cx - nx / 2), sy = Math.floor(cy - ny / 2);
    const ox = W / 2 - (cx - sx) * ts, oy = H / 2 - (cy - sy) * ts;
    ctx.fillStyle = isDk ? '#1A2035' : '#F0F2F5';
    ctx.fillRect(0, 0, W, H);
    for (let x = 0; x < nx; x++) {
      for (let y = 0; y < ny; y++) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        const dx = ox + x * ts, dy = oy + y * ts;
        img.onload = () => { try { ctx.drawImage(img, dx, dy, ts, ts); } catch {} };
        img.onerror = () => {};
        img.src = `${tileBase}/${zoom}/${sx + x}/${sy + y}.png`;
      }
    }
  }, [open, darkMode, bootDone]);

  if (!open || !portalRoot) return null;

  // Derived data
  const ok = summary?.ok;
  const backendResult = summary?.result?.backend;
  const latencyMs = backendResult?.latencyMs;
  const backendPayload = backendResult?.payload || null;
  const backendVersion = String(backendPayload?.version || '').trim();
  const backendBuildTime = String(backendPayload?.build_time || backendPayload?.buildTime || '').trim();
  const backendBuildDate = backendBuildTime ? backendBuildTime.replace('T', ' ').replace('Z', '') : '';
  const backendGitSha = String(backendPayload?.git_sha || backendPayload?.gitSha || '').trim();
  const backendGitShaShort = backendGitSha ? backendGitSha.slice(0, 7) : '';
  const buildInfo = summary?.result?.build || {};
  const buildDate = buildInfo.buildTime ? buildInfo.buildTime.replace('T', ' ').replace('Z', '') : '';
  const perfData = summary?.result?.perf;
  const securityData = summary?.result?.security || {};
  const totalMs = summary?.totalMs;
  const apiUrl = summary?.result?.apiUrl || resolveApiUrl();

  // Metric bar percentages
  const apiPct = latencyMs ? Math.min(100, Math.round((latencyMs / 500) * 100)) : 0;
  const loadPct = perfData?.loadMs ? Math.min(100, Math.round((perfData.loadMs / 1000) * 100)) : 0;
  const totalPct = totalMs ? Math.min(100, Math.round((totalMs / 2000) * 100)) : 0;
  const fmtMs = (ms) => ms >= 1000 ? ms.toLocaleString('sv-SE') : String(ms);

  // Boot line data (dynamic, updates as health check results arrive)
  const apiHost = apiUrl.replace(/^https?:\/\//, '');
  const connType = typeof navigator !== 'undefined' && navigator.connection ? navigator.connection.effectiveType : '';
  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine !== false : true;

  const isDk = darkMode;

  const smCss = `
    .kp-sm{--sm-modal:#fff;--sm-card:#fff;--sm-card-border:#E2E8F0;--sm-card-hover:#F0F2F5;--sm-inset:#F0F2F5;--sm-border:#E2E8F0;--sm-border-subtle:#E4E9F0;--sm-text:#1E293B;--sm-body:#475569;--sm-sec:#64748B;--sm-muted:#7E8FA3;--sm-faint:#8D9DB0;--sm-backdrop:rgba(15,23,42,0.4);--sm-header-bg:#0F172A;--sm-footer-bg:#F0F2F5;--sm-green-bg:#ECFDF5;--sm-green-border:#A7F3D0;--sm-blue-tint:rgba(59,130,246,0.08);--sm-shadow:0 25px 60px -12px rgba(0,0,0,0.12),0 0 0 1px #E2E8F0}
    .kp-sm.kp-dark{--sm-modal:#111827;--sm-card:#1A2035;--sm-card-border:#293247;--sm-card-hover:#1F2A42;--sm-inset:#151C2E;--sm-border:#293247;--sm-border-subtle:#232D42;--sm-text:#E2E8F0;--sm-body:#94A3B8;--sm-sec:#7E8FA3;--sm-muted:#64748B;--sm-faint:#4A5568;--sm-backdrop:rgba(0,0,0,0.6);--sm-header-bg:#060A14;--sm-footer-bg:#0D1220;--sm-green-bg:rgba(5,150,105,0.12);--sm-green-border:rgba(5,150,105,0.25);--sm-blue-tint:rgba(59,130,246,0.12);--sm-shadow:0 25px 60px -12px rgba(0,0,0,0.5),0 0 0 1px #293247}
    .kp-sm .sm-backdrop{position:fixed;inset:0;background:var(--sm-backdrop);backdrop-filter:blur(8px);z-index:100;border:none;cursor:default;animation:smFadeIn .3s ease}
    @keyframes smFadeIn{from{opacity:0}to{opacity:1}}
    .kp-sm .sm-modal{position:fixed;inset:0;z-index:101;display:grid;place-items:center;padding:24px;pointer-events:none}
    .kp-sm .sm-inner{width:920px;max-width:100%;background:var(--sm-modal);border-radius:14px;box-shadow:var(--sm-shadow);overflow:hidden;animation:smSlideUp .4s cubic-bezier(.16,1,.3,1);max-height:90vh;overflow-y:auto;pointer-events:auto}
    @keyframes smSlideUp{from{opacity:0;transform:translateY(24px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}
    .kp-sm .sm-header{position:relative;padding:18px 24px 14px;background:var(--sm-header-bg);overflow:hidden}
    .kp-sm .sm-header::after{content:'';position:absolute;bottom:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#3B82F6,#06B6D4,#8B5CF6);z-index:3}
    .kp-sm .sm-topo{position:absolute;inset:0;z-index:0;pointer-events:none}
    .kp-sm .sm-topo circle{fill:none;stroke:#3B82F6;stroke-width:.8;opacity:.1}
    .kp-sm.kp-dark .sm-topo circle{stroke:#60A5FA;opacity:.07}
    .kp-sm .sm-header-top{display:flex;align-items:flex-start;justify-content:space-between;position:relative;z-index:2}
    .kp-sm .sm-title{font-size:18px;font-weight:700;color:#E2E8F0;letter-spacing:-.3px;margin:0}
    .kp-sm .sm-subtitle{font-size:13px;color:rgba(255,255,255,0.45);margin-top:2px;font-family:'JetBrains Mono',monospace}
    .kp-sm .sm-header-actions{display:flex;gap:8px}
    .kp-sm .sm-icon-btn{width:32px;height:32px;border-radius:6px;border:1px solid rgba(255,255,255,0.1);background:rgba(255,255,255,0.05);color:rgba(255,255,255,0.6);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all 150ms ease;padding:0;font-size:0}
    .kp-sm .sm-icon-btn:hover{background:rgba(255,255,255,0.1);color:#fff;border-color:rgba(255,255,255,0.2)}
    .kp-sm .sm-theme-toggle{display:flex;border:1px solid rgba(255,255,255,0.1);border-radius:6px;overflow:hidden}
    .kp-sm .sm-theme-btn{width:32px;height:32px;background:transparent;border:none;color:rgba(255,255,255,0.4);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all 150ms ease;padding:0}
    .kp-sm .sm-theme-btn:first-child{border-right:1px solid rgba(255,255,255,0.1)}
    .kp-sm .sm-theme-btn.active{background:rgba(255,255,255,0.1);color:#fff}
    .kp-sm .sm-theme-btn:hover:not(.active){color:rgba(255,255,255,0.7)}
    .kp-sm .sm-status-banner{display:flex;align-items:center;gap:10px;margin-top:10px;padding:7px 12px;border-radius:10px;position:relative;z-index:2}
    .kp-sm .sm-status-banner.ok{background:rgba(5,150,105,0.12);border:1px solid rgba(5,150,105,0.2)}
    .kp-sm .sm-status-banner.error{background:rgba(220,38,38,0.12);border:1px solid rgba(220,38,38,0.2)}
    .kp-sm .sm-status-banner.pending{background:rgba(59,130,246,0.12);border:1px solid rgba(59,130,246,0.2)}
    .kp-sm .sm-status-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;animation:smPulseDot 2s ease-in-out infinite}
    .kp-sm .sm-status-dot.green{background:#34D399;box-shadow:0 0 8px rgba(52,211,153,0.6)}
    .kp-sm .sm-status-dot.red{background:#F87171;box-shadow:0 0 8px rgba(248,113,113,0.6)}
    .kp-sm .sm-status-dot.blue{background:#60A5FA;box-shadow:0 0 8px rgba(96,165,250,0.6)}
    @keyframes smPulseDot{0%,100%{opacity:.7}50%{opacity:1}}
    .kp-sm .sm-status-text{font-size:13px;font-weight:500}
    .kp-sm .sm-status-text.green{color:#34D399}
    .kp-sm .sm-status-text.red{color:#F87171}
    .kp-sm .sm-status-text.blue{color:#60A5FA}
    .kp-sm .sm-status-latency{margin-left:auto;font-family:'JetBrains Mono',monospace;font-size:12px;font-weight:500;color:rgba(52,211,153,0.7)}
    .kp-sm .sm-body{padding:16px 24px 18px;display:grid;grid-template-columns:1fr 1fr;gap:14px}
    .kp-sm .sm-section.full-width{grid-column:1/-1}
    .kp-sm .sm-section-header{display:flex;align-items:center;gap:8px;margin-bottom:6px}
    .kp-sm .sm-section-icon{width:20px;height:20px;border-radius:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
    .kp-sm .sm-section-icon.blue{background:rgba(59,130,246,0.1);color:#3B82F6}
    .kp-sm .sm-section-icon.green{background:rgba(5,150,105,0.1);color:#059669}
    .kp-sm .sm-section-icon.purple{background:rgba(124,58,237,0.1);color:#7C3AED}
    .kp-sm .sm-section-icon.amber{background:rgba(217,119,6,0.1);color:#D97706}
    .kp-sm .sm-section-label{font-size:11px;font-weight:600;letter-spacing:.6px;text-transform:uppercase;color:var(--sm-sec)}
    .kp-sm .sm-card{background:var(--sm-card);border:1px solid var(--sm-card-border);border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.04)}
    .kp-sm .sm-diag-row{display:flex;align-items:center;justify-content:space-between;padding:8px 14px;border-bottom:1px solid var(--sm-border-subtle);transition:background 150ms ease}
    .kp-sm .sm-diag-row:last-child{border-bottom:none}
    .kp-sm .sm-diag-row:hover{background:var(--sm-card-hover)}
    .kp-sm .sm-diag-label{font-size:13px;font-weight:500;color:var(--sm-text)}
    .kp-sm .sm-diag-meta{font-family:'JetBrains Mono',monospace;font-size:11px;color:var(--sm-muted);margin-top:2px}
    .kp-sm .sm-badge{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:100px;font-size:12px;font-weight:600;font-family:'JetBrains Mono',monospace}
    .kp-sm .sm-badge-ok{background:var(--sm-green-bg);color:#059669;border:1px solid var(--sm-green-border)}
    .kp-sm .sm-badge-error{background:rgba(220,38,38,0.08);color:#DC2626;border:1px solid rgba(220,38,38,0.2)}
    .kp-sm .sm-badge-pending{background:var(--sm-blue-tint);color:#3B82F6;border:1px solid rgba(59,130,246,0.2)}
    .kp-sm .sm-badge-dot{width:6px;height:6px;border-radius:50%;background:currentColor}
    .kp-sm .sm-metric-wrap{padding:9px 14px;border-bottom:1px solid var(--sm-border-subtle)}
    .kp-sm .sm-metric-wrap:last-child{border-bottom:none}
    .kp-sm .sm-metric-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:5px}
    .kp-sm .sm-metric-label{font-size:12px;font-weight:500;color:var(--sm-body)}
    .kp-sm .sm-metric-value{font-size:12px;font-weight:600;font-family:'JetBrains Mono',monospace;color:var(--sm-text)}
    .kp-sm .sm-metric-bar{height:6px;background:var(--sm-inset);border-radius:100px;overflow:hidden}
    .kp-sm .sm-metric-fill{height:100%;border-radius:100px;animation:smBarGrow 1.2s cubic-bezier(.16,1,.3,1) forwards}
    @keyframes smBarGrow{from{width:0 !important}}
    .kp-sm .fill-green{background:linear-gradient(90deg,#059669,#34D399)}
    .kp-sm .fill-blue{background:linear-gradient(90deg,#2563EB,#60A5FA)}
    .kp-sm .fill-amber{background:linear-gradient(90deg,#D97706,#FBBF24)}
    .kp-sm .sm-kv-grid{display:grid;grid-template-columns:1fr 1fr}
    .kp-sm .sm-kv-item{padding:7px 14px;border-bottom:1px solid var(--sm-border-subtle);transition:background 150ms ease}
    .kp-sm .sm-kv-item:hover{background:var(--sm-card-hover)}
    .kp-sm .sm-kv-item:nth-last-child(-n+2){border-bottom:none}
    .kp-sm .sm-kv-item:nth-child(odd){border-right:1px solid var(--sm-border-subtle)}
    .kp-sm .sm-kv-label{font-size:11px;font-weight:500;color:var(--sm-muted);text-transform:uppercase;letter-spacing:.3px;margin-bottom:3px}
    .kp-sm .sm-kv-value{font-size:13px;font-weight:600;color:var(--sm-text);font-family:'JetBrains Mono',monospace}
    .kp-sm .sm-kv-value.accent{color:#2563EB}
    .kp-sm.kp-dark .sm-kv-value.accent{color:#60A5FA}
    .kp-sm .sm-kv-item.full{grid-column:1/-1;border-right:none}
    .kp-sm .sm-sec-grid{display:grid;grid-template-columns:1fr 1fr}
    .kp-sm .sm-sec-item{display:flex;align-items:center;justify-content:space-between;padding:7px 14px;border-bottom:1px solid var(--sm-border-subtle);transition:background 150ms ease}
    .kp-sm .sm-sec-item:hover{background:var(--sm-card-hover)}
    .kp-sm .sm-sec-item:nth-child(odd){border-right:1px solid var(--sm-border-subtle)}
    .kp-sm .sm-sec-item:nth-last-child(-n+2){border-bottom:none}
    .kp-sm .sm-sec-label{font-size:12px;color:var(--sm-body);font-weight:500}
    .kp-sm .sm-sec-tag{font-size:11px;font-weight:600;font-family:'JetBrains Mono',monospace;padding:2px 8px;border-radius:4px}
    .kp-sm .sm-tag-secure{background:var(--sm-green-bg);color:#059669}
    .kp-sm .sm-tag-info{background:var(--sm-blue-tint);color:#2563EB}
    .kp-sm.kp-dark .sm-tag-info{color:#60A5FA}
    .kp-sm .sm-map-container{position:relative;border-radius:12px;border:1px solid var(--sm-card-border);overflow:hidden;flex:1;min-height:160px}
    .kp-sm .sm-map-pin{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);display:flex;flex-direction:column;align-items:center;pointer-events:none}
    .kp-sm .sm-map-pin-pulse{width:40px;height:40px;border-radius:50%;background:rgba(59,130,246,0.2);animation:smPinPulse 2.5s ease-in-out infinite;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}
    @keyframes smPinPulse{0%,100%{transform:translate(-50%,-50%) scale(1);opacity:.3}50%{transform:translate(-50%,-50%) scale(1.6);opacity:0}}
    .kp-sm .sm-map-pin-dot{width:14px;height:14px;border-radius:50%;background:#3B82F6;border:3px solid #fff;box-shadow:0 2px 8px rgba(37,99,235,0.4);position:relative;z-index:2}
    .kp-sm .sm-map-pin-label{margin-top:6px;padding:3px 10px;background:var(--sm-card);border:1px solid var(--sm-card-border);border-radius:6px;font-family:'JetBrains Mono',monospace;font-size:11px;font-weight:600;color:var(--sm-text);white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.1);position:relative;z-index:2}
    .kp-sm .sm-map-badge{position:absolute;bottom:10px;left:10px;display:flex;align-items:center;gap:6px;padding:5px 10px;background:var(--sm-card);border:1px solid var(--sm-card-border);border-radius:6px;font-family:'JetBrains Mono',monospace;font-size:11px;font-weight:600;color:var(--sm-text);z-index:3}
    .kp-sm .sm-footer{padding:10px 24px;border-top:1px solid var(--sm-border);display:flex;align-items:center;justify-content:space-between;background:var(--sm-footer-bg)}
    .kp-sm .sm-region-info{font-size:11.5px;color:var(--sm-body);margin-top:6px;line-height:1.5;padding:0}
    .kp-sm .sm-footer-left{display:flex;align-items:center;gap:6px;font-size:12px;color:var(--sm-muted);font-family:'JetBrains Mono',monospace}
    .kp-sm .sm-footer-link{font-size:12px;font-weight:600;color:#3B82F6;text-decoration:none;display:flex;align-items:center;gap:4px;transition:color 150ms ease}
    .kp-sm .sm-footer-link:hover{color:#2563EB}
    .kp-sm .sm-section:nth-child(1){animation:smFadeInUp .4s ease .1s both}
    .kp-sm .sm-section:nth-child(2){animation:smFadeInUp .4s ease .15s both}
    .kp-sm .sm-section:nth-child(3){animation:smFadeInUp .4s ease .2s both}
    .kp-sm .sm-section:nth-child(4){animation:smFadeInUp .4s ease .25s both}
    .kp-sm .sm-section:nth-child(5){animation:smFadeInUp .4s ease .3s both}
    @keyframes smFadeInUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    @media(max-width:700px){.kp-sm .sm-body{grid-template-columns:1fr}.kp-sm .sm-section.full-width{grid-column:1}}
    .kp-sm .sm-boot-phase{background:#0A0E17;padding:40px 36px 36px;min-height:380px;display:flex;flex-direction:column;justify-content:center;transition:all .5s ease}
    .kp-sm .sm-boot-phase.collapsed{min-height:0;padding:0;max-height:0;overflow:hidden;opacity:0}
    .kp-sm .sm-boot-terminal{font-family:'JetBrains Mono',monospace;font-size:13px;color:#4ADE80}
    .kp-sm .sm-boot-line{opacity:0;transform:translateY(4px);margin-bottom:5px;line-height:1.7;transition:opacity .25s ease,transform .25s ease}
    .kp-sm .sm-boot-line.show{opacity:1;transform:translateY(0)}
    .kp-sm .sm-boot-line .bm{color:#1E7A45}
    .kp-sm .sm-boot-line .bok{color:#4ADE80}
    .kp-sm .sm-boot-line .bv{color:#60A5FA}
    .kp-sm .sm-boot-line .bw{color:#FBBF24}
    .kp-sm .sm-boot-line .ba{color:#A78BFA}
    .kp-sm .sm-boot-cursor{display:inline-block;width:7px;height:14px;background:#4ADE80;vertical-align:middle;margin-left:2px;animation:smBlink .8s step-end infinite}
    @keyframes smBlink{50%{opacity:0}}
    .kp-sm .sm-boot-progress{margin-top:20px;width:100%;height:2px;background:rgba(74,222,128,0.08);border-radius:1px;overflow:hidden}
    .kp-sm .sm-boot-progress-fill{height:100%;width:0;border-radius:1px;background:linear-gradient(90deg,#4ADE80,#60A5FA);transition:width .3s ease}
    .kp-sm .sm-content-phase{opacity:0;max-height:0;overflow:hidden;transition:opacity .4s ease,max-height .6s ease}
    .kp-sm .sm-content-phase.show{opacity:1;max-height:3000px;overflow:visible}

  `;

  return createPortal(
    <div className={`kp-sm${isDk ? ' kp-dark' : ''}`} style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <style>{smCss}</style>

      {/* Backdrop */}
      <button type="button" className="sm-backdrop" onClick={onClose} aria-label="Stäng" />

      {/* Modal */}
      <div className="sm-modal">
        <div className="sm-inner">

          {/* Boot Phase */}
          <div className={`sm-boot-phase${bootDone ? ' collapsed' : ''}`}>
            <div className="sm-boot-terminal">
              <div className={`sm-boot-line${visibleLines > 0 ? ' show' : ''}`}>
                <span className="bm">$</span> kanplan-diagnostik <span className="ba">--env=production</span>
              </div>
              <div className={`sm-boot-line${visibleLines > 1 ? ' show' : ''}`}>
                <span className="bm">[init]</span> Ansluter till <span className="bv">{apiHost}</span>...
              </div>
              <div className={`sm-boot-line${visibleLines > 2 ? ' show' : ''}`}>
                <span className="bm">[auth]</span> TLS 1.3 handshake <span className="bok">OK</span>
              </div>
              <div className={`sm-boot-line${visibleLines > 3 ? ' show' : ''}`}>
                <span className="bm">[ping]</span> Backend health: <span className="bv">{latencyMs ? `${latencyMs} ms` : '...'}</span>{latencyMs ? <>{' '}<span className="bok">OK</span></> : null}
              </div>
              <div className={`sm-boot-line${visibleLines > 4 ? ' show' : ''}`}>
                <span className="bm">[info]</span> Version <span className="bv">{buildInfo.version || '...'}</span> &middot; commit <span className="bv">{backendGitShaShort || buildInfo.commitShaShort || '...'}</span>
              </div>
              <div className={`sm-boot-line${visibleLines > 5 ? ' show' : ''}`}>
                <span className="bm">[geo]</span> Region: <span className="bv">eu-north-1</span> (Stockholm) <span className="bok">OK</span>
              </div>
              <div className={`sm-boot-line${visibleLines > 6 ? ' show' : ''}`}>
                <span className="bm">[net]</span> Nätverk: {isOnline ? 'Online' : 'Offline'}{connType ? ` · ${connType}` : ''} <span className="bok">OK</span>
              </div>
              <div className={`sm-boot-line${visibleLines > 7 ? ' show' : ''}`}>
                <span className="bm">[perf]</span> Sidladdning: <span className="bw">{perfData?.loadMs ? `${perfData.loadMs} ms` : '...'}</span> &middot; Total: <span className="bw">{totalMs ? `${fmtMs(totalMs)} ms` : '...'}</span>
              </div>
              <div className={`sm-boot-line${visibleLines > 8 ? ' show' : ''}`}>
                <span className="bok">{ok === true ? 'Alla system operativa.' : ok === false ? 'Driftstörning identifierad.' : 'Kontrollerar...'}</span><span className="sm-boot-cursor"></span>
              </div>
            </div>
            <div className="sm-boot-progress">
              <div className="sm-boot-progress-fill" style={{ width: `${bootProgress}%` }}></div>
            </div>
          </div>

          {/* Content Phase */}
          <div className={`sm-content-phase${bootDone ? ' show' : ''}`}>

          {/* Header */}
          <div className="sm-header">
            <svg className="sm-topo" viewBox="0 0 920 130" preserveAspectRatio="xMidYMid slice">
              <circle cx="700" cy="75" r="18"/><circle cx="700" cy="75" r="36"/><circle cx="700" cy="75" r="54"/>
              <circle cx="700" cy="75" r="72"/><circle cx="700" cy="75" r="90"/><circle cx="700" cy="75" r="108"/>
              <circle cx="700" cy="75" r="126"/>
              <circle cx="200" cy="40" r="14"/><circle cx="200" cy="40" r="32"/><circle cx="200" cy="40" r="50"/>
              <circle cx="200" cy="40" r="68"/><circle cx="200" cy="40" r="86"/>
              <circle cx="460" cy="110" r="20"/><circle cx="460" cy="110" r="38"/><circle cx="460" cy="110" r="56"/>
              <circle cx="460" cy="110" r="74"/>
            </svg>

            <div className="sm-header-top">
              <div>
                <h2 className="sm-title">Driftstatus</h2>
                <div className="sm-subtitle">{apiUrl.replace(/^https?:\/\//, '')}</div>
              </div>
              <div className="sm-header-actions">
                <div className="sm-theme-toggle">
                  <button type="button" className={`sm-theme-btn${!isDk ? ' active' : ''}`} onClick={() => isDk && onToggleDark?.()} title="Ljust läge">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                  <button type="button" className={`sm-theme-btn${isDk ? ' active' : ''}`} onClick={() => !isDk && onToggleDark?.()} title="Mörkt läge">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M13.5 9.5a5.5 5.5 0 01-7-7A5.5 5.5 0 108 14a5.48 5.48 0 005.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
                <button type="button" className="sm-icon-btn" onClick={onClose} title="Stäng">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
              </div>
            </div>

            <div className={`sm-status-banner ${ok === true ? 'ok' : ok === false ? 'error' : 'pending'}`}>
              <div className={`sm-status-dot ${ok === true ? 'green' : ok === false ? 'red' : 'blue'}`} />
              <span className={`sm-status-text ${ok === true ? 'green' : ok === false ? 'red' : 'blue'}`}>
                {ok === true ? 'Alla system operativa' : ok === false ? 'Driftstörning identifierad' : 'Kontrollerar system...'}
              </span>
              {ok === true && latencyMs != null && (
                <span className="sm-status-latency">{latencyMs} ms</span>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="sm-body">

            {/* Diagnostik */}
            <div className="sm-section">
              <div className="sm-section-header">
                <div className="sm-section-icon blue">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M1 8h3l2-5 3 10 2-5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <span className="sm-section-label">Diagnostik</span>
              </div>
              <div className="sm-card">
                {steps.map((s) => (
                  <div key={s.key} className="sm-diag-row">
                    <div>
                      <div className="sm-diag-label">{s.label}</div>
                      {s.meta && <div className="sm-diag-meta">{s.meta}</div>}
                    </div>
                    <span className={`sm-badge ${s.status === 'ok' ? 'sm-badge-ok' : s.status === 'error' ? 'sm-badge-error' : 'sm-badge-pending'}`}>
                      <span className="sm-badge-dot" />
                      {s.status === 'ok' ? 'OK' : s.status === 'error' ? 'Fel' : s.status === 'running' ? 'Pågår' : 'Väntar'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Prestanda */}
            <div className="sm-section">
              <div className="sm-section-header">
                <div className="sm-section-icon green">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 1v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/></svg>
                </div>
                <span className="sm-section-label">Prestanda</span>
              </div>
              <div className="sm-card">
                <div className="sm-metric-wrap">
                  <div className="sm-metric-header">
                    <span className="sm-metric-label">API-svarstid</span>
                    <span className="sm-metric-value">{latencyMs ? `${latencyMs} ms` : '—'}</span>
                  </div>
                  <div className="sm-metric-bar"><div className="sm-metric-fill fill-green" style={{ width: `${apiPct}%` }} /></div>
                </div>
                <div className="sm-metric-wrap">
                  <div className="sm-metric-header">
                    <span className="sm-metric-label">Sidladdning</span>
                    <span className="sm-metric-value">{perfData?.loadMs ? `${perfData.loadMs} ms` : '—'}</span>
                  </div>
                  <div className="sm-metric-bar"><div className="sm-metric-fill fill-blue" style={{ width: `${loadPct}%` }} /></div>
                </div>
                <div className="sm-metric-wrap">
                  <div className="sm-metric-header">
                    <span className="sm-metric-label">Total tid</span>
                    <span className="sm-metric-value">{totalMs ? `${fmtMs(totalMs)} ms` : '—'}</span>
                  </div>
                  <div className="sm-metric-bar"><div className="sm-metric-fill fill-amber" style={{ width: `${totalPct}%` }} /></div>
                </div>
              </div>
            </div>

            {/* Build Info */}
            <div className="sm-section">
              <div className="sm-section-header">
                <div className="sm-section-icon purple">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="14" height="14" rx="3" stroke="currentColor" strokeWidth="1.5"/><path d="M5 5l3 3-3 3M9 11h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <span className="sm-section-label">Build Info</span>
              </div>
              <div className="sm-card">
                <div className="sm-kv-grid">
                  <div className="sm-kv-item">
                    <div className="sm-kv-label">Miljö</div>
                    <div className="sm-kv-value">{process.env.REACT_APP_BUILD_ENV || buildInfo.nodeEnv || '—'}</div>
                  </div>
                  <div className="sm-kv-item">
                    <div className="sm-kv-label">Version</div>
                    <div className="sm-kv-value accent">{buildInfo.version || '—'}</div>
                  </div>
                  <div className="sm-kv-item">
                    <div className="sm-kv-label">Frontend build</div>
                    <div className="sm-kv-value">{buildDate || '—'}</div>
                  </div>
                  <div className="sm-kv-item">
                    <div className="sm-kv-label">Backend build</div>
                    <div className="sm-kv-value">{backendBuildDate || '—'}</div>
                  </div>
                  <div className="sm-kv-item">
                    <div className="sm-kv-label">Commit</div>
                    <div className="sm-kv-value">{backendGitShaShort || buildInfo.commitShaShort || '—'}</div>
                  </div>
                  <div className="sm-kv-item">
                    <div className="sm-kv-label">Region</div>
                    <div className="sm-kv-value">eu-north-1</div>
                  </div>
                  <div className="sm-kv-item full">
                    <div className="sm-kv-label">Backend version</div>
                    <div className="sm-kv-value" style={{ fontSize: 11, wordBreak: 'break-all' }}>{backendVersion || '—'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Region Map */}
            <div className="sm-section" style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="sm-section-header">
                <div className="sm-section-icon blue">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M1 8h14M8 1a11 11 0 014 7 11 11 0 01-4 7 11 11 0 01-4-7A11 11 0 018 1z" stroke="currentColor" strokeWidth="1.5"/></svg>
                </div>
                <span className="sm-section-label">Region</span>
              </div>
              <div className="sm-map-container">
                <canvas ref={mapCanvasRef} width={600} height={300} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                <div className="sm-map-pin">
                  <div className="sm-map-pin-pulse" />
                  <div className="sm-map-pin-dot" />
                  <div className="sm-map-pin-label">Stockholm</div>
                </div>
                <div className="sm-map-badge">
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#3B82F6' }}>SE</span>
                  AWS eu-north-1
                </div>
              </div>
              <div className="sm-region-info">
                All data lagras i AWS eu-north-1 (Stockholm). Regionen avgör den fysiska platsen för infrastrukturen och säkerställer att data hanteras inom EU i enlighet med GDPR.
              </div>
            </div>

            {/* Säkerhet & Drift */}
            <div className="sm-section full-width">
              <div className="sm-section-header">
                <div className="sm-section-icon amber">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 1a5 5 0 015 5v2a3 3 0 01-3 3H6a3 3 0 01-3-3V6a5 5 0 015-5z" stroke="currentColor" strokeWidth="1.5"/><path d="M6 11v2a2 2 0 004 0v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </div>
                <span className="sm-section-label">Säkerhet & Drift</span>
              </div>
              <div className="sm-card">
                <div className="sm-sec-grid">
                  <div className="sm-sec-item">
                    <span className="sm-sec-label">Transport (sida)</span>
                    <span className={`sm-sec-tag ${securityData.pageTransport === 'HTTPS' ? 'sm-tag-secure' : 'sm-tag-info'}`}>{securityData.pageTransport || '—'}</span>
                  </div>
                  <div className="sm-sec-item">
                    <span className="sm-sec-label">Transport (API)</span>
                    <span className={`sm-sec-tag ${securityData.apiTransport === 'HTTPS' ? 'sm-tag-secure' : 'sm-tag-info'}`}>{securityData.apiTransport || '—'}</span>
                  </div>
                  <div className="sm-sec-item">
                    <span className="sm-sec-label">Secure context</span>
                    <span className={`sm-sec-tag ${securityData.secureContext ? 'sm-tag-secure' : 'sm-tag-info'}`}>{securityData.secureContext == null ? '—' : securityData.secureContext ? 'Ja' : 'Nej'}</span>
                  </div>
                  <div className="sm-sec-item">
                    <span className="sm-sec-label">Cookies</span>
                    <span className="sm-sec-tag sm-tag-info">{securityData.cookiesEnabled == null ? '—' : securityData.cookiesEnabled ? 'Tillåtna' : 'Blockerade'}</span>
                  </div>
                  <div className="sm-sec-item">
                    <span className="sm-sec-label">Backend status</span>
                    <span className={`sm-sec-tag ${backendResult?.ok ? 'sm-tag-secure' : 'sm-tag-info'}`}>{backendResult ? (backendResult.ok ? `OK (${latencyMs} ms)` : 'Fel') : '—'}</span>
                  </div>
                  <div className="sm-sec-item">
                    <span className="sm-sec-label">Datasuveränitet</span>
                    <span className="sm-sec-tag sm-tag-info">EU / GDPR</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="sm-footer">
            <div className="sm-footer-left">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M8 4v4l3 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              {running ? 'Beräknar...' : totalMs ? `Klar på ${fmtMs(totalMs)} ms` : ''}
            </div>
            <a href={supportMailto} className="sm-footer-link">
              Kontakta support
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
          </div>

          </div>{/* end content-phase */}

        </div>
      </div>
    </div>,
    portalRoot
  );
}

function getFrontendBuildInfo() {
  const nodeEnv = process.env.NODE_ENV || '';
  const version = (process.env.REACT_APP_VERSION || process.env.REACT_APP_RELEASE || '').trim();
  const buildTime = (process.env.REACT_APP_BUILD_TIME || '').trim();
  const commitSha = (process.env.REACT_APP_GIT_SHA || process.env.REACT_APP_COMMIT_SHA || '').trim();

  const commitShaShort = commitSha ? commitSha.slice(0, 8) : '';

  // In local dev we still want something readable.
  const fallbackTime = (() => {
    try { return new Date().toISOString(); } catch { return ''; }
  })();

  return {
    nodeEnv: nodeEnv || 'unknown',
    version: version || (nodeEnv === 'development' ? 'dev' : ''),
    buildTime: buildTime || (nodeEnv === 'development' ? fallbackTime : ''),
    commitShaShort: commitShaShort || (nodeEnv === 'development' ? 'local' : ''),
  };
}
